import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Message, SendMessageData } from '@flock-types/chat.types';
import { auth } from '@/config/firebase';
import { useAuth } from '@/context/AuthContext';
import type { Conversation } from '@/hooks/useAllConversations';
import useGroupChat from '@/hooks/useGroupChat';
import useGroupMembers from '@/hooks/useGroupMembers';
import { useNavigate } from 'react-router-dom';
import useChatGroupActions from '@/hooks/useChatGroupActions';
import { pushRecentEmoji } from '@/lib/recentEmojis';
import { safeHttpUrl } from '@/lib/safeUrl';
import MessageList from './MessageList';
import MessageContextMenu from './MessageContextMenu';
import MessageRichContent from './MessageRichContent';
import { consumePendingReply, stashPendingReply } from './pendingReplyStash';
import PinnedMessagesBar from './PinnedMessagesBar';
import ImageLightbox, { type LightboxImage } from './ImageLightbox';
import { requestNotificationPermission } from '@/components/NotificationsManager';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import ChatComposerLock from '@/components/lock/ChatComposerLock';
import MessageComposer, {
  type ComposerEditState,
  type ComposerReplyState,
} from '../composer/MessageComposer';

interface Props {
  conversation: Conversation;
  /**
   * True when ChatLayout auto-selected this conversation (bare /chat landing).
   * Delays mark-as-read until the user interacts or ~2.5s pass, so the unread
   * badge in the conversation list doesn't vanish before it's seen.
   */
  autoSelected?: boolean;
  /** Search-result jump target (message may be outside the loaded window). */
  searchJumpId?: string | null;
  onSearchJumpConsumed?: () => void;
}

/**
 * Active conversation: pinned bar + message list + composer.
 * Mounted only when the org context matches the conversation's org — the
 * parent gates on that so the Firestore subscription always fires with
 * matching custom claims.
 */
export default function ConversationPane({
  conversation,
  autoSelected,
  searchJumpId,
  onSearchJumpConsumed,
}: Props) {
  // Freeze the unread count at open time for the "New messages" divider
  const initialUnreadRef = useRef(conversation.unreadCount);

  // Auto-selected conversations hold read receipts until the user engages
  // (interaction or 2.5s dwell) — otherwise the unread badge clears on mount.
  const [engaged, setEngaged] = useState(!autoSelected);
  useEffect(() => {
    if (engaged) return;
    const t = setTimeout(() => setEngaged(true), 2500);
    return () => clearTimeout(t);
  }, [engaged]);
  // Same-key re-selection: clicking the already-auto-selected conversation
  // clears router state without remounting — treat it as engagement.
  useEffect(() => {
    if (!autoSelected) setEngaged(true);
  }, [autoSelected]);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [anchorJump, setAnchorJump] = useState(false);

  // Search jumps may target messages outside the loaded window — anchor mode
  // makes useGroupChat fetch a context window via the REST context endpoint.
  useEffect(() => {
    if (searchJumpId) {
      setAnchorJump(true);
      setScrollTarget(searchJumpId);
      onSearchJumpConsumed?.();
    }
  }, [searchJumpId, onSearchJumpConsumed]);
  const [replyTo, setReplyTo] = useState<ComposerReplyState | null>(() => {
    const pending = consumePendingReply(conversation.chatGroupId);
    return pending ? { message: pending } : null;
  });
  const [editState, setEditState] = useState<ComposerEditState | null>(null);
  const [actionMessage, setActionMessage] = useState<Message | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [droppedFiles, setDroppedFiles] = useState<File[] | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  const { members } = useGroupMembers(conversation.groupId);
  const { organizations } = useAuth();
  const { isOrgLocked } = useOrgLockState();
  const orgLocked = isOrgLocked(conversation.organizationId);

  // Message docs carry Firebase UIDs + a write-time photo URL that expires;
  // the members list (PG ids + names) is re-presigned per fetch. userName is
  // the join key — rules enforce it matches the groupMembers doc at send.
  const photoByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      if (m.profileImageUrl) map.set(m.userName, m.profileImageUrl);
    }
    return map;
  }, [members]);

  const resolveAvatar = useCallback(
    (message: Message) => photoByName.get(message.userName) ?? message.userProfileImageUrl,
    [photoByName]
  );

  // Leader of THIS community group — mirrors the Firestore rules' check
  // (userRoles/{uid}_{groupId}_group_leader), derived from /auth/me roles.
  const isLeader = useMemo(() => {
    const membership = organizations.find(
      (o) => o.organization_id === conversation.organizationId
    );
    return Boolean(
      membership?.roles.some(
        (r) =>
          r.role === 'group_leader' &&
          (r.scope_id === conversation.groupId || r.scope_type === 'organization')
      )
    );
  }, [organizations, conversation.organizationId, conversation.groupId]);

  const {
    messages,
    pinnedMessages,
    loading,
    hasMore,
    loadMore,
    sendMessage,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    setPinned,
    setTyping,
    error,
  } = useGroupChat({
    groupId: conversation.groupId,
    groupName: conversation.groupName,
    chatGroupId: conversation.chatGroupId,
    chatGroupName: conversation.chatGroupId ? conversation.name : undefined,
    scrollToMessageId: scrollTarget,
    anchorScrollToMessage: anchorJump,
    writeReceipts: engaged,
  });

  const handleSend = useCallback(
    async (data: SendMessageData) => {
      // First send is a user gesture — a safe moment to ask for notifications
      requestNotificationPermission();
      await sendMessage(data);
    },
    [sendMessage]
  );

  const handleEditSubmit = useCallback(
    async (messageId: string, newText: string) => {
      await editMessage(messageId, newText);
    },
    [editMessage]
  );

  const handleEditLast = useCallback(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const lastOwn = messages.find((m) => m.userId === uid && m.type === 'text');
    if (lastOwn) {
      setReplyTo(null);
      setEditState({ message: lastOwn });
    }
  }, [messages]);

  const handleToggleReaction = useCallback(
    (message: Message, emoji: string, reacted: boolean) => {
      (reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)).catch((e) =>
        console.warn('[Chat] Reaction toggle failed:', e)
      );
    },
    [addReaction, removeReaction]
  );

  // All images in the conversation, oldest→newest, for lightbox navigation
  const lightboxImages = useMemo((): LightboxImage[] => {
    return [...messages]
      .reverse()
      .filter((m) => (m.type === 'image' || m.type === 'gif') && m.content?.mediaUrl)
      .flatMap((m) => {
        const url = safeHttpUrl(m.content?.giphyOriginalUrl) || safeHttpUrl(m.content?.mediaUrl);
        return url ? [{ url, fileName: m.content?.fileName }] : [];
      });
  }, [messages]);

  const renderRichContent = useCallback(
    (message: Message, own: boolean) => (
      <MessageRichContent
        message={message}
        own={own}
        onImageClick={(url) => {
          const idx = lightboxImages.findIndex((img) => img.url === url);
          setLightboxIndex(idx >= 0 ? idx : null);
        }}
      />
    ),
    [lightboxImages]
  );

  const handleDelete = useCallback(
    (message: Message) => {
      if (!window.confirm('Delete this message? This cannot be undone.')) return;
      deleteMessage(message.id).catch((e) => console.warn('[Chat] Delete failed:', e));
    },
    [deleteMessage]
  );

  const handleTogglePin = useCallback(
    (message: Message) => {
      setPinned(message.id, !message.isPinned).catch((e) =>
        console.warn('[Chat] Pin toggle failed:', e)
      );
    },
    [setPinned]
  );

  const navigate = useNavigate();
  const { createDM } = useChatGroupActions(conversation.groupId);

  /** "Privately Reply" / "Message {name}": open (or create) the DM with the
   *  sender; a pending-reply stash seeds the quote in the DM's composer. */
  const openDmWithSender = useCallback(
    async (message: Message, withQuote: boolean) => {
      setActionMessage(null);
      try {
        // DM endpoint accepts a Firebase UID and resolves it server-side
        const { chatGroup } = await createDM(message.userId);
        if (withQuote) stashPendingReply(chatGroup.id, message);
        navigate(`/chat/${conversation.groupId}/${chatGroup.id}`);
      } catch (e) {
        console.warn('[Chat] Open DM failed:', e);
      }
    },
    [createDM, navigate, conversation.groupId]
  );

  const handleMenuReact = useCallback(
    (message: Message, emoji: string) => {
      const uid = auth.currentUser?.uid;
      const reacted = Boolean(uid && message.reactions[emoji]?.includes(uid));
      if (!reacted) pushRecentEmoji(emoji);
      handleToggleReaction(message, emoji, reacted);
      setActionMessage(null);
    },
    [handleToggleReaction]
  );

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="max-w-sm text-center text-sm text-flock-navy-500">{error}</p>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col bg-flock-navy-50"
      onPointerDownCapture={() => setEngaged(true)}
      onWheelCapture={() => setEngaged(true)}
      onKeyDownCapture={() => setEngaged(true)}
      onTouchStartCapture={() => setEngaged(true)}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          dragCounterRef.current += 1;
          setDragActive(true);
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDragLeave={() => {
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setDragActive(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragCounterRef.current = 0;
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length > 0) setDroppedFiles(files);
      }}
    >
      {dragActive && (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center border-4 border-dashed border-flock-navy-400 bg-flock-navy-900/40">
          <p className="rounded-xl bg-white px-6 py-3 text-sm font-semibold text-flock-navy-800 shadow-xl">
            Drop a photo to share it
          </p>
        </div>
      )}

      {actionMessage && (
        <MessageContextMenu
          message={actionMessage}
          own={actionMessage.userId === auth.currentUser?.uid}
          isDM={conversation.type === 'dm'}
          isLeader={isLeader}
          onReact={(emoji) => handleMenuReact(actionMessage, emoji)}
          onReply={() => {
            setEditState(null);
            setReplyTo({ message: actionMessage });
            setActionMessage(null);
          }}
          onPrivateReply={() => openDmWithSender(actionMessage, true)}
          onMessageUser={() => openDmWithSender(actionMessage, false)}
          onEdit={() => {
            setReplyTo(null);
            setEditState({ message: actionMessage });
            setActionMessage(null);
          }}
          onTogglePin={() => {
            handleTogglePin(actionMessage);
            setActionMessage(null);
          }}
          onDelete={() => {
            setActionMessage(null);
            handleDelete(actionMessage);
          }}
          onClose={() => setActionMessage(null)}
        />
      )}

      {lightboxIndex !== null && (
        <ImageLightbox
          images={lightboxImages}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <PinnedMessagesBar pinnedMessages={pinnedMessages} onJumpTo={(id) => { setAnchorJump(false); setScrollTarget(id); }} />

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-flock-navy-400">
          Loading messages…
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center">
          <p className="text-sm font-medium text-flock-navy-600">No messages yet</p>
          <p className="text-xs text-flock-navy-400">Be the first to say hello 👋</p>
        </div>
      ) : (
        <MessageList
          messages={messages}
          hasMore={hasMore}
          loadMore={loadMore}
          initialUnreadCount={initialUnreadRef.current}
          onReplyClick={(id) => { setAnchorJump(false); setScrollTarget(id); }}
          onToggleReaction={handleToggleReaction}
          renderRichContent={renderRichContent}
          onOpenActions={setActionMessage}
          resolveAvatar={resolveAvatar}
          scrollToMessageId={scrollTarget}
        />
      )}

      {orgLocked ? (
        // Paused org: history stays readable; only new messages are off.
        <ChatComposerLock organizationId={conversation.organizationId} />
      ) : (
        <MessageComposer
          groupId={conversation.groupId}
          incomingFiles={droppedFiles}
          onIncomingFilesConsumed={() => setDroppedFiles(null)}
          onSend={handleSend}
          onEditSubmit={handleEditSubmit}
          members={members}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editState={editState}
          onCancelEdit={() => setEditState(null)}
          onEditLastRequested={handleEditLast}
          onTyping={setTyping}
          placeholder={`Message ${conversation.type === 'dm' ? conversation.name : conversation.type === 'everyone' ? 'everyone in ' + conversation.groupName : conversation.name}`}
        />
      )}
    </div>
  );
}
